import _ from 'lodash';
import React, { useState } from 'react';
import { Formik } from 'formik';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

// Data
import { buildApplyAttendanceDefaultToAssessments, buildUpdateCompany, handleCompanyLogoUpload } from '../../../lib/services/company.service';
import { TIMEZONE_NAME_OPTIONS } from '../../../lib/timezone.constants';
import { UpdateCompanyForm } from './update-company-form.component';
import { IUpdateCompanyFields, IUpdateCompanyProps } from './update-company-form.interfaces';
import { validationSchema } from './update-company-form.validation';
import { getAuthUser } from '../../../lib/services/auth.service';
import { canEditAttendanceDefault } from '../../Attendance/attendance.permissions';
import { useCompanyHasReportCards } from '../../../lib/stores/company.store';

// Components

dayjs.extend(utc)
dayjs.extend(timezone)

interface IUpdateCompanyContainer {
  company: ICompany;
  refetch: Function;
}

export const UpdateCompanyFormContainer: React.FC<IUpdateCompanyContainer> = ({ company, refetch }) => {
  const { updateCompany } = buildUpdateCompany();
  const { applyAttendanceDefaultToAssessments } = buildApplyAttendanceDefaultToAssessments();
  const user = getAuthUser();

  const [submitError, setSubmitError] = useState<string | null>(null);

  const userCanEditAttendanceDefault = canEditAttendanceDefault();
  const hasReportCards = useCompanyHasReportCards();
  const canEditReportCardDefault = userCanEditAttendanceDefault && hasReportCards;

  const setCompanyLogo = (companyId: string, files: FileList, token: string | undefined | null) => {
    return handleCompanyLogoUpload(companyId, files, token)
      .then(() => refetch());
  }

  const timezone = _.find(TIMEZONE_NAME_OPTIONS, option => option.value === company.timezone) || TIMEZONE_NAME_OPTIONS[0];
  const initialValues: IUpdateCompanyFields = {
    name: company.name,
    timezone,
    communicationEmail: company.communicationEmail,
    companyWebsite: company.companyWebsite,
    evaluationGroupName: company.evaluationGroupName,
    introLink: company.introLink,
    isAttendanceRequestedDefault: company.isAttendanceRequestedDefault ?? false,
    reportCardsIncludeNonConcludedDefault: company.reportCardsIncludeNonConcludedDefault ?? false,
    retainJerseyAssignments: company.retainJerseyAssignments ?? false,
    hasCalibrationVideos: company.hasCalibrationVideos ?? false,
    maxCalibrationSkillsPerAssignment: company.maxCalibrationSkillsPerAssignment ?? 10,
    minCalibrationSkillsPerAssignment: company.minCalibrationSkillsPerAssignment ?? 3,
    maxCalibrationSkillsPerVideoPerAssignment: company.maxCalibrationSkillsPerVideoPerAssignment ?? 2,
    periodLengthDays: company.periodLengthDays ?? 30,
    maxCalibrationSkillsPerPeriod: company.maxCalibrationSkillsPerPeriod ?? 25,
    deliveryWindowHours: company.deliveryWindowHours ?? 2,
    assignmentCooldownHours: company.assignmentCooldownHours ?? 4,
    assignmentDueDays: company.assignmentDueDays ?? 7,
  };

  const handleSubmit = (values: IUpdateCompanyFields, { setSubmitting, resetForm }: any) => {
    setSubmitError(null);

    const data: IUpdateCompanyProps = { ..._.omit(values, ['timezone']) };
    data.timezone = values.timezone.value;

    // Strip each default the member can't edit — its input is render-gated on the same check.
    if (!userCanEditAttendanceDefault) {
      delete data.isAttendanceRequestedDefault;
      delete data.retainJerseyAssignments;
    }
    if (!canEditReportCardDefault || values.reportCardsIncludeNonConcludedDefault === initialValues.reportCardsIncludeNonConcludedDefault) {
      delete data.reportCardsIncludeNonConcludedDefault;
    }

    updateCompany(data)
      .then(() => {
        // Rebase the dirty baseline onto the saved values (no enableReinitialize here);
        // otherwise the form stays `dirty` and the "Apply default" button stays disabled.
        resetForm({ values });
      })
      .catch((error) => {
        console.error('updateCompany failed:', error);
        setSubmitError("Couldn't save your settings. Please try again.");
        setSubmitting(false);
      });
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {form => (<UpdateCompanyForm
        form={form}
        company={company}
        setCompanyLogo={setCompanyLogo}
        user={user}
        submitError={submitError}
        applyAttendanceDefaultToAssessments={applyAttendanceDefaultToAssessments}
        canEditAttendanceDefault={userCanEditAttendanceDefault}
        canEditReportCardDefault={canEditReportCardDefault}
      />)}
    </Formik>
  );
};

